/**
 * File Drop Zone Component
 *
 * Handles drag-and-drop file import from desktop with context-aware drop zones
 * and import dialog for ambiguous drops.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { Upload, AlertCircle } from 'lucide-react';
import { ImportDialog } from './dialogs/ImportDialog';
import { ImportManager, type ImportOptions } from '@/data/import/ImportManager';
import { useStorageAdapter } from '@/hooks/useStorageAdapter';
import { useTauriFileDrop } from '@/hooks/useTauriFileDrop';
import { toast } from 'sonner';
import type { StorageFile } from '@/data/adapters/types';

export type DropContext =
  | 'EDITOR_NEW'
  | 'FOLDER_IMPORT'
  | 'PROJECT_ROOT'
  | 'SIDEBAR';

interface FileDropZoneProps {
  /** The context where files are being dropped */
  context: DropContext;
  /** Target folder ID for FOLDER_IMPORT context */
  targetFolderId?: string;
  /** Whether the drop zone is currently active */
  isActive?: boolean;
  /** Callback when files are successfully imported */
  onImport?: (files: StorageFile[]) => void;
  /** Custom class names */
  className?: string;
  /** Children elements */
  children?: React.ReactNode;
  /** Whether to show visual drop indicators */
  showIndicators?: boolean;
}

interface PendingImport {
  files: File[];
  context: DropContext;
  targetFolderId?: string;
}

const SUPPORTED_EXTENSIONS = ['.md', '.txt', '.json', '.mdx', '.phrasewright'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

export function FileDropZone({
  context,
  targetFolderId,
  onImport,
  className,
  children,
  showIndicators = true,
}: FileDropZoneProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [isValidDrop, setIsValidDrop] = useState(true);
  const [pendingImport, setPendingImport] = useState<PendingImport | null>(
    null
  );
  const [showImportDialog, setShowImportDialog] = useState(false);
  const dropZoneRef = useRef<HTMLDivElement>(null);
  const dragCounter = useRef(0);
  const adapter = useStorageAdapter();

  // Set up Tauri file drop handling
  useTauriFileDrop(async (files) => {
    // Handle files dropped via Tauri
    const validation = validateFiles(files);

    if (!validation.valid) {
      toast.error(validation.reason || 'Invalid files');
      return;
    }

    if (shouldShowImportDialog(files, context)) {
      setPendingImport({ files, context, targetFolderId });
      setShowImportDialog(true);
    } else {
      await handleDirectImport(files);
    }
  }, true);

  // Validate if files can be dropped
  const validateFiles = useCallback(
    (files: FileList | File[]): { valid: boolean; reason?: string } => {
      const fileArray = Array.from(files);

      // Check if any files
      if (fileArray.length === 0) {
        return { valid: false, reason: 'No files to import' };
      }

      // Check file extensions
      const unsupportedFiles = fileArray.filter((file) => {
        const extension = '.' + file.name.split('.').pop()?.toLowerCase();
        return !SUPPORTED_EXTENSIONS.includes(extension);
      });

      if (unsupportedFiles.length > 0) {
        return {
          valid: false,
          reason: `Unsupported file types: ${unsupportedFiles.map((f) => f.name).join(', ')}`,
        };
      }

      // Check file sizes
      const oversizedFiles = fileArray.filter(
        (file) => file.size > MAX_FILE_SIZE
      );
      if (oversizedFiles.length > 0) {
        return {
          valid: false,
          reason: `Files too large (max 10MB): ${oversizedFiles.map((f) => f.name).join(', ')}`,
        };
      }

      return { valid: true };
    },
    []
  );

  // Handle drag enter
  const handleDragEnter = useCallback(
    (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current++;

      if (e.dataTransfer?.types.includes('Files')) {
        setIsDragging(true);

        // Check if drop would be valid
        const items = e.dataTransfer.items;
        const files: File[] = [];

        for (let i = 0; i < items.length; i++) {
          if (items[i].kind === 'file') {
            const file = items[i].getAsFile();
            if (file) files.push(file);
          }
        }

        const validation = validateFiles(files);
        setIsValidDrop(validation.valid);
      }
    },
    [validateFiles]
  );

  // Handle drag leave
  const handleDragLeave = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;

    if (dragCounter.current === 0) {
      setIsDragging(false);
      setIsValidDrop(true);
    }
  }, []);

  // Handle drag over
  const handleDragOver = useCallback(
    (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (e.dataTransfer) {
        e.dataTransfer.dropEffect = isValidDrop ? 'copy' : 'none';
      }
    },
    [isValidDrop]
  );

  // Determine if import dialog should be shown
  const shouldShowImportDialog = (
    files: File[],
    context: DropContext
  ): boolean => {
    // Always show dialog for multiple files
    if (files.length > 1) return true;

    // Show dialog for ambiguous contexts
    if (context === 'PROJECT_ROOT') return true;

    // Show dialog if file type is ambiguous (not clearly markdown)
    const file = files[0];
    const extension = '.' + file.name.split('.').pop()?.toLowerCase();
    if (extension === '.txt' || extension === '.json') return true;

    return false;
  };

  // Handle file drop
  const handleDrop = useCallback(
    async (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current = 0;
      setIsDragging(false);

      if (!e.dataTransfer?.files || e.dataTransfer.files.length === 0) {
        return;
      }

      const files = Array.from(e.dataTransfer.files);
      const validation = validateFiles(files);

      if (!validation.valid) {
        toast.error(validation.reason || 'Invalid files');
        return;
      }

      // Check if we need to show import dialog
      if (shouldShowImportDialog(files, context)) {
        setPendingImport({ files, context, targetFolderId });
        setShowImportDialog(true);
      } else {
        // Direct import for simple cases
        await handleDirectImport(files);
      }
    },
    [context, targetFolderId, validateFiles]
  );

  // Handle direct import (no dialog)
  const handleDirectImport = async (files: File[]) => {
    if (!adapter) {
      toast.error('Storage adapter not available');
      return;
    }

    const importedFiles: StorageFile[] = [];

    for (const file of files) {
      try {
        // Determine file type based on context and extension
        const fileType = determineFileType(file, context);

        // Create storage file from imported file
        const storageFile = await createStorageFile(
          file,
          fileType,
          targetFolderId
        );

        // Save to storage
        const savedFile = await adapter.saveFile?.(storageFile);
        if (!savedFile) {
          throw new Error(
            'Failed to save file - saveFile method not available'
          );
        }
        importedFiles.push(savedFile);

        toast.success(`Imported ${file.name}`);
      } catch (error) {
        console.error('Failed to import file:', error);
        toast.error(`Failed to import ${file.name}`);
      }
    }

    if (importedFiles.length > 0) {
      onImport?.(importedFiles);
    }
  };

  // Determine file type based on context and file extension
  const determineFileType = (
    file: File,
    context: DropContext
  ): 'phrase' | 'document' => {
    const extension = '.' + file.name.split('.').pop()?.toLowerCase();

    // Context-based determination
    if (context === 'EDITOR_NEW') {
      // In editor, prefer document for markdown files
      return extension === '.md' || extension === '.mdx'
        ? 'document'
        : 'phrase';
    }

    // Default based on extension
    return extension === '.md' || extension === '.mdx' ? 'document' : 'phrase';
  };

  // Create storage file from imported file
  const createStorageFile = async (
    file: File,
    type: 'phrase' | 'document',
    folderId?: string
  ): Promise<StorageFile> => {
    const content = await file.text();
    const name = file.name.replace(/\.[^/.]+$/, ''); // Remove extension

    const storageFile: StorageFile = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      name,
      content,
      type,
      tags: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      revision: 1,
    };

    if (folderId) {
      storageFile.metadata = { folderId };
    }

    return storageFile;
  };

  // Handle import from dialog
  const handleImportFromDialog = async (options: ImportOptions) => {
    if (!pendingImport || !adapter) return;

    setShowImportDialog(false);

    const importManager = new ImportManager(adapter);

    for (const file of pendingImport.files) {
      try {
        // Handle .phrasewright files specially
        if (file.name.endsWith('.phrasewright')) {
          const result = await importManager.importFile(file, options);
          if (result.success) {
            toast.success(
              `Imported ${result.importedFiles} files from ${file.name}`
            );
          } else {
            toast.error(`Failed to import ${file.name}`);
          }
        } else {
          // Regular file import
          await handleDirectImport([file]);
        }
      } catch (error) {
        console.error('Failed to import file:', error);
        toast.error(`Failed to import ${file.name}`);
      }
    }

    setPendingImport(null);
  };

  // Set up event listeners
  useEffect(() => {
    const element = dropZoneRef.current;
    if (!element) return;

    element.addEventListener('dragenter', handleDragEnter as any);
    element.addEventListener('dragleave', handleDragLeave as any);
    element.addEventListener('dragover', handleDragOver as any);
    element.addEventListener('drop', handleDrop as any);

    return () => {
      element.removeEventListener('dragenter', handleDragEnter as any);
      element.removeEventListener('dragleave', handleDragLeave as any);
      element.removeEventListener('dragover', handleDragOver as any);
      element.removeEventListener('drop', handleDrop as any);
    };
  }, [handleDragEnter, handleDragLeave, handleDragOver, handleDrop]);

  return (
    <>
      <div
        ref={dropZoneRef}
        className={cn(
          'relative',
          isDragging && showIndicators && 'ring-2 ring-offset-2',
          isDragging && isValidDrop && 'ring-green-500',
          isDragging && !isValidDrop && 'ring-red-500',
          className
        )}
      >
        {children}

        {/* Drop overlay */}
        {isDragging && showIndicators && (
          <div
            className={cn(
              'absolute inset-0 z-50 flex items-center justify-center',
              'bg-background/80 backdrop-blur-sm',
              'border-2 border-dashed rounded-lg',
              isValidDrop ? 'border-green-500' : 'border-red-500'
            )}
          >
            <div className="text-center p-4">
              {isValidDrop ? (
                <>
                  <Upload className="w-12 h-12 mx-auto mb-2 text-green-500" />
                  <p className="text-sm font-medium">Drop files to import</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {context === 'FOLDER_IMPORT' && 'Import to folder'}
                    {context === 'EDITOR_NEW' && 'Create new file'}
                    {context === 'PROJECT_ROOT' && 'Import to project'}
                  </p>
                </>
              ) : (
                <>
                  <AlertCircle className="w-12 h-12 mx-auto mb-2 text-red-500" />
                  <p className="text-sm font-medium">Unsupported files</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Only .md, .txt, .json, and .phrasewright files are supported
                  </p>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Import dialog */}
      {showImportDialog && pendingImport && (
        <ImportDialog
          files={pendingImport.files}
          context={pendingImport.context}
          targetFolderId={pendingImport.targetFolderId}
          onImport={handleImportFromDialog}
          onCancel={() => {
            setShowImportDialog(false);
            setPendingImport(null);
          }}
        />
      )}
    </>
  );
}
